import type { IProfileData } from '../types/ILocalStorage';
import { isGuestBrowseProfile, isGuestUser } from '../config/guestMode';
import user from '../helpers/user';
import url from '../helpers/url';
import { parentalPinManagementConstants, ROUTE_CONSTANT } from '../../../../apps/wcp/src/config/constants';

interface IProfileSwitchingFlags {
    userHasParentalPin: boolean;
    isKidsProfile: boolean;
    isKidsProfileLocked: boolean;
    profileNeedsColdStart: boolean;
    profileIsPasswordProtected: boolean;
    urlToOpenTheProfile: string;
    urlToExitTheProfile: string;
}

/**
 * This function handles profile switching logic for a user in an application.
 * It determines the URLs to open or exit a profile based on the user's parental controls,
 * profile characteristics (e.g., kid's mode, parental lock), and the profile's cold start state.
 *
 * @param {Partial<IProfileData>} profile - Partial details of the selected profile.
 * @returns An object containing profile-related flags and computed URLs for opening or exiting the profile.
 */
export function useProfileSwitching(profile: Partial<IProfileData> | null | undefined): IProfileSwitchingFlags {
    if (isGuestUser(user.getUserInfo()) || isGuestBrowseProfile(profile)) {
        return {
            userHasParentalPin: false,
            isKidsProfile: false,
            isKidsProfileLocked: false,
            profileNeedsColdStart: false,
            profileIsPasswordProtected: false,
            urlToOpenTheProfile: url.route(''),
            urlToExitTheProfile: url.route(ROUTE_CONSTANT.SELECT_PROFILE),
        };
    }

    const userHasParentalPin: boolean = user.getParentalLockOn() || false;
    const isKidsProfile: boolean = profile?.kidsMode ?? false;
    const isKidsProfileLocked: boolean = Boolean(profile?.kidsMode && profile?.parentalLockOn);
    const profileNeedsColdStart: boolean = profile?.coldStart === 0;
    const profileIsPasswordProtected: boolean = profile?.passwordProtected ?? false;

    let urlToOpenTheProfile = '';
    let urlToExitTheProfile = '';

    // Determine the URL for opening the profile
    if (isKidsProfile) {
        if (isKidsProfileLocked && !userHasParentalPin) {
            urlToOpenTheProfile =
                url.getDetailsPage(parentalPinManagementConstants.PARENTAL_PIN) + window.btoa('kid-home|add');
        } else {
            urlToOpenTheProfile = profileNeedsColdStart ? ROUTE_CONSTANT.COLD_START : ROUTE_CONSTANT.KIDS_HOME;
        }
    } else if (profileNeedsColdStart) {
        urlToOpenTheProfile = ROUTE_CONSTANT.COLD_START;
    }

    // Determine the URL for exiting the profile
    if (isKidsProfileLocked && userHasParentalPin) {
        urlToExitTheProfile =
            url.getDetailsPage(parentalPinManagementConstants.PARENTAL_PIN) + window.btoa('kid-exit|validate');
    } else {
        urlToExitTheProfile = ROUTE_CONSTANT.SELECT_PROFILE;
    }

    return {
        userHasParentalPin,
        isKidsProfile,
        isKidsProfileLocked,
        profileNeedsColdStart,
        profileIsPasswordProtected,
        urlToOpenTheProfile: url.route(urlToOpenTheProfile),
        urlToExitTheProfile: url.route(urlToExitTheProfile),
    };
}
